'use client';

/**
 * Generic client tab island. Profile edit + my-learning use the same
 * pattern: a strip of clickable tabs across the top, content swap below.
 *
 * <p>Distinct from {@code CourseTabs} (the block) — Tabs takes
 * pre-rendered ReactNode content per tab rather than a data array.
 */
import { useState, type ReactNode } from 'react';
import { useBlockPart } from '@akademi/site-builder-runtime';

export interface TabsProps {
  tabs: Array<{ label: string; content: ReactNode }>;
  defaultIndex?: number;
}

export function Tabs({ tabs, defaultIndex = 0 }: TabsProps) {
  const [active, setActive] = useState(defaultIndex);
  const part = useBlockPart();
  return (
    <div>
      <div
        {...part('tablist')}
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid var(--color-neutral-200, #e4e4e7)',
          marginBottom: 24,
          overflowX: 'auto',
        }}
      >
        {tabs.map((t, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActive(i)}
            {...part('tab', i)}
            style={{
              padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 500,
              background: 'none',
              border: 'none',
              borderBottom:
                active === i
                  ? '2px solid var(--color-text-primary, #18181b)'
                  : '2px solid transparent',
              marginBottom: -1,
              color:
                active === i
                  ? 'var(--color-text-primary, #18181b)'
                  : 'var(--color-text-secondary, #71717a)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontFamily: 'var(--font-family-body, inherit)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div {...part('panel')}>{tabs[active]?.content}</div>
    </div>
  );
}
